import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Configuration, ConfigContextType } from '../types/configuration.types';
import { configurationService } from '../services/configuration.service';

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

interface ConfigProviderProps {
  children: ReactNode;
}

const ConfigProvider: React.FC<ConfigProviderProps> = ({ children }) => {
  const [configurations, setConfigurations] = useState<Record<string, Configuration>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchConfigurations = async () => {
      try {
        setLoading(true);
        setError(null);
        const configs = await configurationService.getConfigurations();
        
        // Convert array to object map for easy lookup
        const configMap = configs.reduce((acc, config) => {
          acc[config.key] = config;
          return acc;
        }, {} as Record<string, Configuration>);
        
        setConfigurations(configMap);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load configurations');
        console.error('Error loading configurations:', err);
        setConfigurations({});
      } finally {
        setLoading(false);
      }
    };

    fetchConfigurations();
  }, []);

  const getConfig = (key: string): string | null => {
    const config = configurations[key];
    return config ? config.value : null;
  };

  const getGroup = (group: string): Configuration[] => {
    return Object.values(configurations).filter(config => config.config_group === group);
  };

  return (
    <ConfigContext.Provider value={{ configurations, loading, error, getConfig, getGroup }}>
      {children}
    </ConfigContext.Provider>
  );
};

export { ConfigProvider };

export const useConfigContext = (): ConfigContextType => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfigContext must be used within a ConfigProvider');
  }
  return context;
};
